/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import {
  COLUMNTYPE_JSON,
  COLUMNTYPE_ARRAY,
  COLUMNTYPE_NUMBER,
  DB2_IDENTIFIER_MAXLENGTH,
  POSTGRESQL_IDENTIFIER_MAXLENGTH,
} from 'constants';

import { defineMessages } from 'react-intl';
import delay from 'lodash/delay';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import moment from 'moment/min/moment-with-locales';
import { validate } from 'jsonschema';

import { getLocalStorageIsDB2 } from 'localStorageUtils';

const timePattern = new RegExp(/^([01]\d|2[0-3]):([0-5]\d)$/);
const messages = defineMessages({
  required: 'Required',
  number: 'Must be a number',
  invalidFormat: '{locale} format',
  timeFormat: 'hh:mm format',
  nameExists: 'Specify a name that does not already exist.',
  specialCharacters: 'Specify a name that does not contain special characters.',
  invalidJSON: 'Invalid JSON. {message}.',
  startGreaterThan: 'Start date > end date',
  positive: 'Must be positive integer',
  maxLength: `Cannot be longer than {maxLength}.`,
  firstCharacter: 'The name’s first character must be a letter',
});

export function scrollErrorIntoView(focus = true) {
  const invalidField = document.querySelector('[data-invalid="true"]');
  if (invalidField) {
    invalidField.scrollIntoView({ behavior: 'smooth' });
    if (focus) {
      delay(() => invalidField.focus(), 250);
    }
    return true;
  }
  return false;
}

export function fieldRequired(value, allValues, props) {
  const {
    intl: { formatMessage },
  } = props;
  if (typeof value === 'string' || Array.isArray(value)) {
    return !isEmpty(value) ? undefined : formatMessage(messages.required);
  }
  if (typeof value === 'number' && isNil(value)) {
    return formatMessage(messages.required);
  }
  return !isNil(value) ? undefined : formatMessage(messages.required);
}

export function validateNumber(value, allValues, props) {
  const {
    intl: { formatMessage },
  } = props;
  return !isEmpty(value) && typeof value !== 'number' ? formatMessage(messages.number) : undefined;
}

export function parseJSON(value) {
  try {
    return JSON.parse(value);
  } catch (e) {
    return value;
  }
}

export function getDecimalSeparator(locale) {
  return (1.1).toLocaleString(locale).substring(1, 2);
}

export function handleParse(value, type, typeForArray, locale) {
  switch (type) {
    case COLUMNTYPE_JSON:
      return parseJSON(value);
    case COLUMNTYPE_NUMBER: {
      const decimalSeparator = locale ? getDecimalSeparator(locale) : '.';
      const partialDigitPattern1 = new RegExp(`\\d*\\${decimalSeparator}$`, 'g');
      const partialDigitPattern2 = new RegExp(`\\d*\\${decimalSeparator}\\d*0$`, 'g');
      if (value && (value.match(partialDigitPattern1) || value.match(partialDigitPattern2))) {
        // partial number, user entering floating point number, eg 123. or 0.00
        return value;
      }
      const valueWithInternalDecimal = value && value.replace(decimalSeparator, '.');
      if (
        Number.isNaN(parseInt(valueWithInternalDecimal, 10)) &&
        Number.isNaN(parseFloat(valueWithInternalDecimal))
      ) {
        return value;
      }
      if (
        !Number.isNaN(parseInt(valueWithInternalDecimal, 10)) &&
        parseInt(valueWithInternalDecimal, 10) === parseFloat(valueWithInternalDecimal)
      ) {
        return parseInt(valueWithInternalDecimal, 10);
      }
      return parseFloat(valueWithInternalDecimal);
    }

    case COLUMNTYPE_ARRAY:
      return isEmpty(value)
        ? []
        : value
            .replace(/,([.a-zA-Z\d\-_])/g, ', $1') // automatically put a space after comma
            .split(', ')
            .map((item, index) => {
              let nestedType = typeForArray;
              if (Array.isArray(nestedType)) {
                nestedType = nestedType[index] || nestedType[0];
              }
              return typeForArray && item.indexOf(',') < 0 ? handleParse(item, nestedType) : item;
            });
    default:
      // trim the string items
      return value;
  }
}

export function handleFormat(value, type, locale) {
  switch (type) {
    case COLUMNTYPE_JSON:
      if (typeof value !== 'string') {
        return JSON.stringify(value, null, '\t');
      }
      return value;
    case COLUMNTYPE_NUMBER:
      if (locale) {
        // if we pass locale, we should locale format numbers
        return value && value.toLocaleString(locale, { maximumFractionDigits: 20 });
      }
      return value;
    case COLUMNTYPE_ARRAY:
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      return value;

    default:
      return value;
  }
}

export function isJSON(value) {
  try {
    JSON.parse(value);
    return { valid: true };
  } catch (e) {
    return { valid: false, message: e.message };
  }
}

export function validateJSON(value, schema, formatMessage) {
  if (isEmpty(value)) {
    return undefined;
  }
  const jsonCheck = isJSON(value);
  if (typeof value === 'string' && !jsonCheck.valid) {
    return formatMessage(messages.invalidJSON, { message: jsonCheck.message });
  }
  return schema && !validate(value, schema).valid
    ? formatMessage(messages.invalidJSON, { message: '' })
    : undefined;
}

// Will not do any more locale specific time parsing, investigated and this is best practice
export function parseTime(duration) {
  if (isEmpty(duration)) {
    return null;
  }
  // Convert to utc before uploading
  return moment(duration, 'HH:mm').utc();
}

export function parseDate(dateString) {
  // Convert to utc before uploading
  return moment(dateString, 'L').utc();
}

export function formatTime(timestamp) {
  return moment(timestamp).format('HH:mm');
}

export function formatDate(timestamp, locale = 'en', format = 'L') {
  moment.locale(locale); // set moment locale
  return moment(timestamp).format(format);
}

export function validateTimePattern(time) {
  const trimmedTime = time && time.trim ? time.trim() : undefined;
  return timePattern.test(trimmedTime);
}

// Time and date validators
export function validateTime(time, allValues, props) {
  if (!time || isEmpty(time)) {
    return undefined;
  }

  const {
    intl: { formatMessage },
  } = props;

  return validateTimePattern(time) ? undefined : formatMessage(messages.timeFormat);
}

export const validateRange = (values, props) => {
  const { radioButtonGroup, startDateInput, startTime, endDateInput, endTime } = values;
  const {
    intl: { formatMessage },
  } = props;
  const errors = {};
  if (radioButtonGroup === 'custom') {
    const startRange =
      startDateInput !== undefined && startTime !== undefined
        ? `${startDateInput.trim()} ${startTime.trim()}`
        : '';
    const endRange =
      endDateInput !== undefined && endTime !== undefined
        ? `${endDateInput.trim()} ${endTime.trim()}`
        : '';
    if (moment(startRange, 'L HH:mm:ss') >= moment(endRange, 'L HH:mm:ss')) {
      errors.startDateInput = formatMessage(messages.startGreaterThan);
    }
    errors.startDateInput = errors.startDateInput || fieldRequired(startDateInput, values, props);
    errors.endDateInput = fieldRequired(endDateInput, values, props);
  }
  return errors;
};

/**
 * Checks if the date format is in local format DD/MM/YYYY or YYYY/MM/DD
 * @param {string} value
 * @returns {Boolean} false means not valid
 */
export function validateDatePattern(value) {
  return moment(value, 'L').isValid();
}

/**
 * @param {string} value
 * @param {Array<string>} values
 * @param {Object} props
 * @returns {string} formatted invalid message
 */
export function validateDate(value, values, props) {
  const {
    intl: { formatMessage },
  } = props;
  return validateDatePattern(value)
    ? undefined
    : formatMessage(messages.invalidFormat, {
        locale: moment.localeData().longDateFormat('L'),
      });
}

export function validateUniqueName(name, items, field, props) {
  const {
    intl: { formatMessage },
  } = props;
  // validate that we don't have a name collision
  return find(
    items,
    item => typeof item[field] === 'string' && item[field].toUpperCase() === name.toUpperCase()
  )
    ? formatMessage(messages.nameExists)
    : undefined;
}

export function validateSpecialCharacters(
  name,
  props,
  allowedSpecialCharsRegExp = '[^a-zA-Z\\d_-]'
) {
  const {
    intl: { formatMessage },
  } = props;
  const specialChars = new RegExp(allowedSpecialCharsRegExp);
  return specialChars.test(name) ? formatMessage(messages.specialCharacters) : undefined;
}

export function validateMaxLength(
  name,
  props,
  maxLength = getLocalStorageIsDB2() ? DB2_IDENTIFIER_MAXLENGTH : POSTGRESQL_IDENTIFIER_MAXLENGTH
) {
  const {
    intl: { formatMessage },
  } = props;

  return name && name.length > maxLength
    ? formatMessage(messages.maxLength, { maxLength })
    : undefined;
}

export function validatePositive(number, props) {
  const {
    intl: { formatMessage },
  } = props;
  return number <= 0 || !Number.isInteger(number) ? formatMessage(messages.positive) : undefined;
}

export function validateFirstChar(name, props, allowedFirstCharsRegExp = '^[a-zA-Z]+') {
  const {
    intl: { formatMessage },
  } = props;
  const firstChars = new RegExp(allowedFirstCharsRegExp);
  return firstChars.test(name) ? undefined : formatMessage(messages.firstCharacter);
}
